/* ============================================================
   Agri Solsec — Quote PDF writer
   Used by: assets/pump-calculator.js

   Emits a real PDF file client-side, with no external library.
   PDF 1.4, A4, using the base-14 Helvetica faces that every
   reader ships with, so nothing has to be embedded.

   Text is transcoded to WinAnsi (the encoding declared on the
   font); characters outside it are swapped for safe equivalents
   so a stray glyph can never corrupt the document.
   ============================================================ */

(function () {
  'use strict';

  var PAGE_W = 595.28; /* A4 at 72 dpi */
  var PAGE_H = 841.89;
  var MARGIN = 48;
  var CONTENT_W = PAGE_W - MARGIN * 2;

  var INK = [0.1, 0.1, 0.1];
  var MUTED = [0.42, 0.42, 0.44];
  var FAINT = [0.55, 0.55, 0.58];
  var RED = [0.984, 0.008, 0];
  var GREEN = [0.106, 0.369, 0.125];
  var RULE = [0.87, 0.87, 0.89];

  /* ---------- Helvetica advance widths (units per 1000 em) ---------- */

  var W_REGULAR = [
    278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
    556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556,
    1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
    667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556,
    333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556,
    556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
  ];

  var W_BOLD = [
    278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278,
    556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611,
    975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
    667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556,
    333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611,
    611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584,
  ];

  /* Widths for the WinAnsi high characters this document actually uses */
  var W_HIGH = {
    0x85: 1000, 0x92: 222, 0x95: 350, 0x96: 556, 0x97: 1000,
    0xb0: 400, 0xb2: 333, 0xb3: 333, 0xb7: 278, 0xd7: 584,
  };

  /* Unicode that has a WinAnsi slot, or a sensible ASCII stand-in */
  var TRANSCODE = {
    '—': '\x97', '–': '\x96', '‘': "'", '’': '\x92',
    '“': '"', '”': '"', '…': '\x85', '•': '\x95',
    ' ': ' ', '≈': '~', '≥': '>=', '≤': '<=',
    '·': '\xb7', '²': '\xb2', '³': '\xb3', '×': '\xd7',
    '°': '\xb0', '−': '-',
  };

  function toWinAnsi(input) {
    var text = String(input === null || input === undefined ? '' : input);
    var out = '';
    for (var i = 0; i < text.length; i++) {
      var ch = text[i];
      if (Object.prototype.hasOwnProperty.call(TRANSCODE, ch)) {
        out += TRANSCODE[ch];
        continue;
      }
      var code = ch.charCodeAt(0);
      /* Drop anything with no WinAnsi representation (emoji, CJK, …) */
      if (code <= 0xff) out += ch;
    }
    return out;
  }

  function widthOf(text, size, bold) {
    var table = bold ? W_BOLD : W_REGULAR;
    var total = 0;
    for (var i = 0; i < text.length; i++) {
      var code = text.charCodeAt(i);
      var w;
      if (code >= 32 && code <= 126) w = table[code - 32];
      else if (W_HIGH[code]) w = W_HIGH[code];
      else w = 556;
      total += w;
    }
    return (total / 1000) * size;
  }

  function escapePdf(text) {
    return text.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
  }

  function fmt(n) {
    return Math.round(n * 100) / 100;
  }

  /* ============================================================
     Document builder — a top-down cursor over one or more pages
     ============================================================ */

  function Doc() {
    this.pages = [];
    this.ops = null;
    this.cursor = 0;
    this.newPage();
  }

  Doc.prototype.newPage = function () {
    this.ops = [];
    this.pages.push(this.ops);
    this.cursor = MARGIN;
  };

  /* Start a new page when the next block wouldn't fit */
  Doc.prototype.reserve = function (height) {
    if (this.cursor + height > PAGE_H - MARGIN - 28) {
      this.newPage();
      return true;
    }
    return false;
  };

  Doc.prototype.text = function (value, x, opts) {
    var o = opts || {};
    var size = o.size || 9.5;
    var bold = !!o.bold;
    var color = o.color || INK;
    var tracking = o.tracking || 0;
    var body = toWinAnsi(value);
    var y = PAGE_H - (o.y === undefined ? this.cursor : o.y);
    var drawX = x;

    if (o.align === 'right') {
      drawX = x - (widthOf(body, size, bold) + tracking * Math.max(0, body.length - 1));
    } else if (o.align === 'center') {
      drawX = x - (widthOf(body, size, bold) + tracking * Math.max(0, body.length - 1)) / 2;
    }

    this.ops.push(
      'BT',
      '/' + (bold ? 'F2' : 'F1') + ' ' + size + ' Tf',
      color[0] + ' ' + color[1] + ' ' + color[2] + ' rg',
      tracking ? tracking + ' Tc' : '0 Tc',
      '1 0 0 1 ' + fmt(drawX) + ' ' + fmt(y) + ' Tm',
      '(' + escapePdf(body) + ') Tj',
      '0 Tc',
      'ET'
    );
  };

  Doc.prototype.rect = function (x, yTop, w, h, color) {
    this.ops.push(
      color[0] + ' ' + color[1] + ' ' + color[2] + ' rg',
      fmt(x) + ' ' + fmt(PAGE_H - yTop - h) + ' ' + fmt(w) + ' ' + fmt(h) + ' re',
      'f'
    );
  };

  Doc.prototype.rule = function (yTop, color) {
    var c = color || RULE;
    this.ops.push(
      c[0] + ' ' + c[1] + ' ' + c[2] + ' RG',
      '0.7 w',
      fmt(MARGIN) + ' ' + fmt(PAGE_H - yTop) + ' m',
      fmt(PAGE_W - MARGIN) + ' ' + fmt(PAGE_H - yTop) + ' l',
      'S'
    );
  };

  /* Greedy wrap; returns the lines so callers can measure first */
  Doc.prototype.wrap = function (value, maxWidth, size, bold) {
    var words = toWinAnsi(value).split(/\s+/).filter(Boolean);
    var lines = [];
    var line = '';
    for (var i = 0; i < words.length; i++) {
      var candidate = line ? line + ' ' + words[i] : words[i];
      if (widthOf(candidate, size, bold) > maxWidth && line) {
        lines.push(line);
        line = words[i];
      } else {
        line = candidate;
      }
    }
    if (line) lines.push(line);
    return lines;
  };

  Doc.prototype.paragraph = function (value, x, maxWidth, opts) {
    var o = opts || {};
    var size = o.size || 8.5;
    var leading = o.leading || size * 1.45;
    var lines = this.wrap(value, maxWidth, size, !!o.bold);
    for (var i = 0; i < lines.length; i++) {
      this.text(lines[i], x, { size: size, bold: o.bold, color: o.color, align: o.align });
      this.cursor += leading;
    }
    return lines.length;
  };

  Doc.prototype.sectionTitle = function (label) {
    this.reserve(42);
    this.cursor += 8;
    this.text(label.toUpperCase(), MARGIN, { size: 8, bold: true, color: FAINT, tracking: 1.1 });
    this.cursor += 6;
    this.rule(this.cursor);
    this.cursor += 11;
  };

  /* Label on the left, value hard against the right margin */
  Doc.prototype.row = function (label, value, opts) {
    var o = opts || {};
    this.reserve(18);
    this.text(label, MARGIN, { size: 9.5, color: o.labelColor || MUTED });
    this.text(value, PAGE_W - MARGIN, {
      size: o.size || 9.5,
      bold: o.bold === undefined ? true : o.bold,
      color: o.color || INK,
      align: 'right',
    });
    this.cursor += o.gap || 13;
  };

  /* ============================================================
     Serialisation
     ============================================================ */

  function serialise(doc, meta) {
    var objects = [];
    var pageCount = doc.pages.length;

    /* 1 catalog, 2 pages, 3 F1, 4 F2, then page/content pairs */
    var firstPageObj = 5;

    var kids = [];
    for (var i = 0; i < pageCount; i++) kids.push(firstPageObj + i * 2 + ' 0 R');

    objects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
    objects[2] = '<< /Type /Pages /Kids [' + kids.join(' ') + '] /Count ' + pageCount + ' >>';
    objects[3] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>';
    objects[4] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>';

    for (var p = 0; p < pageCount; p++) {
      var pageObj = firstPageObj + p * 2;
      var contentObj = pageObj + 1;
      var stream = doc.pages[p].join('\n');

      objects[pageObj] =
        '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + PAGE_W + ' ' + PAGE_H + ']' +
        ' /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >>' +
        ' /Contents ' + contentObj + ' 0 R >>';

      objects[contentObj] =
        '<< /Length ' + stream.length + ' >>\nstream\n' + stream + '\nendstream';
    }

    var infoObj = objects.length;
    objects[infoObj] =
      '<< /Title (' + escapePdf(toWinAnsi(meta.title)) + ')' +
      ' /Author (' + escapePdf(toWinAnsi(meta.author)) + ')' +
      ' /Creator (Agri Solsec pump calculator) >>';

    /* Assemble, tracking byte offsets for the xref table */
    var out = '%PDF-1.4\n';
    var offsets = [];
    for (var n = 1; n < objects.length; n++) {
      offsets[n] = out.length;
      out += n + ' 0 obj\n' + objects[n] + '\nendobj\n';
    }

    var xrefStart = out.length;
    var count = objects.length;
    out += 'xref\n0 ' + count + '\n';
    out += '0000000000 65535 f \n';
    for (var m = 1; m < count; m++) {
      out += ('0000000000' + offsets[m]).slice(-10) + ' 00000 n \n';
    }
    out +=
      'trailer\n<< /Size ' + count + ' /Root 1 0 R /Info ' + infoObj + ' 0 R >>\n' +
      'startxref\n' + xrefStart + '\n%%EOF';

    /* Every char is <= 0xFF after transcoding, so 1 char == 1 byte */
    var bytes = new Uint8Array(out.length);
    for (var b = 0; b < out.length; b++) bytes[b] = out.charCodeAt(b) & 0xff;
    return new Blob([bytes], { type: 'application/pdf' });
  }

  /* ============================================================
     The quote document
     ============================================================ */

  function generate(data) {
    var doc = new Doc();
    var company = data.company || {};

    /* --- Masthead --- */
    doc.rect(0, 0, PAGE_W, 5, RED);
    doc.cursor = 62;
    doc.text(company.name || 'AGRI SOLSEC', MARGIN, { size: 21, bold: true, color: INK, tracking: 0.4 });

    doc.text('QUOTE REF', PAGE_W - MARGIN, { size: 7.5, bold: true, color: FAINT, tracking: 1, align: 'right', y: 54 });
    doc.text(data.ref, PAGE_W - MARGIN, { size: 10, bold: true, color: INK, align: 'right', y: 66 });
    doc.text('DATE', PAGE_W - MARGIN, { size: 7.5, bold: true, color: FAINT, tracking: 1, align: 'right', y: 80 });
    doc.text(data.date, PAGE_W - MARGIN, { size: 10, color: INK, align: 'right', y: 92 });

    doc.cursor = 78;
    doc.text('Solar Pump Quotation', MARGIN, { size: 10.5, color: MUTED });
    doc.cursor = 102;
    doc.rule(doc.cursor, INK);
    doc.cursor += 6;

    /* --- Customer --- */
    var c = data.customer || {};
    doc.sectionTitle('Prepared for');
    doc.row('Name', c.name || '-');
    doc.row('Phone / WhatsApp', c.phone || '-');
    if (c.email) doc.row('Email', c.email);
    if (c.town) doc.row('Town / farm', c.town);
    if (c.province) doc.row('Province', c.province);

    /* --- Site --- */
    var s = data.site || {};
    doc.sectionTitle('Borehole & site');
    doc.row('Borehole depth', s.depth + ' m');
    doc.row('Delivery distance', s.distance + ' m');
    doc.row('Rise to tank', s.rise + ' m');
    doc.row('Pipe size', s.pipeSize + ' mm');
    doc.row('Water needed', s.litresPerHour + ' L/h');
    if (s.friction !== null) doc.row('Pipe friction head', s.friction + ' m');
    if (s.totalHead !== null) doc.row('Total head', s.totalHead + ' m');

    /* --- Pump --- */
    if (data.pump) {
      var p = data.pump;
      doc.sectionTitle('Recommended pump');
      doc.row('Model', p.model);
      doc.row('Drive', p.drive + ' \xb7 ' + p.watts + ' W');
      doc.row('Outlet', p.outlet);
      if (p.delivers) doc.row('Delivers', p.delivers);
    }

    /* --- Yield --- */
    if (data.yield) {
      doc.sectionTitle('Estimated daily water yield');
      doc.row('Winter (worst case)', data.yield.winter + ' L/day', { color: GREEN });
      doc.row('Summer (best case)', data.yield.summer + ' L/day', { color: GREEN });
      doc.row('Based on', data.yield.province + ' peak sun hours', { bold: false, color: MUTED });
    }

    /* --- Line items --- */
    if (data.lines && data.lines.length) {
      doc.sectionTitle('Quotation');
      for (var i = 0; i < data.lines.length; i++) {
        var line = data.lines[i];
        doc.reserve(30);
        doc.text(line.label, MARGIN, { size: 9.5, bold: true, color: INK });
        doc.text(line.amount, PAGE_W - MARGIN, { size: 10, bold: true, color: INK, align: 'right' });
        doc.cursor += 12;
        if (line.detail) {
          doc.text(line.detail, MARGIN, { size: 8.5, color: FAINT });
          doc.cursor += 11;
        }
        doc.cursor += 4;
      }

      doc.reserve(48);
      doc.cursor += 4;
      doc.rule(doc.cursor, INK);
      doc.cursor += 20;
      var totalLabel = 'INDICATIVE TOTAL';
      var totalLabelW =
        widthOf(toWinAnsi(totalLabel), 11, true) + 0.5 * (totalLabel.length - 1);
      doc.text(totalLabel, MARGIN, { size: 11, bold: true, color: INK, tracking: 0.5 });
      doc.text('(VAT incl)', MARGIN + totalLabelW + 7, { size: 8.5, color: FAINT });
      doc.text(data.total, PAGE_W - MARGIN, { size: 17, bold: true, color: RED, align: 'right' });
      doc.cursor += 18;

      if (data.porNote) {
        doc.paragraph(
          'Total excludes the 16 mm2 cable - we will confirm it on your formal quote.',
          MARGIN, CONTENT_W, { size: 8.5, color: MUTED }
        );
      }
    }

    /* --- Customer notes --- */
    if (data.notes) {
      doc.sectionTitle('Notes from customer');
      doc.paragraph(data.notes, MARGIN, CONTENT_W, { size: 9.5, color: MUTED, leading: 14 });
    }

    /* --- Closing: pinned to the foot of the page so a short quote
           never spills a near-empty second page --- */
    var disclaimer =
      data.disclaimer ||
      'Indicative pricing, VAT incl. The water figure is read off the pump performance curve at your total head. Final sizing and price are confirmed by our team.';
    var contact = [company.phone, company.email, company.website, company.address]
      .filter(Boolean)
      .join('   \xb7   ');

    var disclaimerLines = doc.wrap(disclaimer, CONTENT_W, 8, false).length;
    var footerH =
      16 + disclaimerLines * 11.5 + 8 + (contact ? 12 : 0) + (company.footer ? 12 : 0);

    if (doc.cursor + 24 > PAGE_H - MARGIN - footerH) doc.newPage();
    doc.cursor = PAGE_H - MARGIN - footerH;

    doc.rule(doc.cursor);
    doc.cursor += 16;
    doc.paragraph(disclaimer, MARGIN, CONTENT_W, { size: 8, color: FAINT, leading: 11.5 });

    doc.cursor += 8;
    if (contact) {
      doc.text(contact, MARGIN, { size: 8, color: MUTED });
      doc.cursor += 12;
    }
    if (company.footer) {
      doc.text(company.footer, MARGIN, { size: 8, color: FAINT });
    }

    /* Page numbers only matter once there is more than one page */
    if (doc.pages.length > 1) {
      for (var pageIndex = 0; pageIndex < doc.pages.length; pageIndex++) {
        var saved = doc.ops;
        doc.ops = doc.pages[pageIndex];
        doc.text(
          'Page ' + (pageIndex + 1) + ' of ' + doc.pages.length,
          PAGE_W - MARGIN,
          { size: 7.5, color: FAINT, align: 'right', y: PAGE_H - 26 }
        );
        doc.ops = saved;
      }
    }

    return serialise(doc, {
      title: 'Agri Solsec Solar Pump Quote ' + data.ref,
      author: company.name || 'Agri Solsec',
    });
  }

  window.AgriPumpQuotePdf = { generate: generate };
})();
